import React, { useRef, useEffect, useState } from 'react';

const OurFocus = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const focusAreas = [
    {
      id: 1,
      title: 'Intelligent Products',
      subtitle: 'AI-Driven Enterprise Solutions',
      description: 'We create AI-driven systems that transform enterprise data into actionable insights.',
      details: 'Our products combine machine learning with elegant, human-centered design for real-world applications.',
      features: ['Natural Language Processing', 'Predictive Analytics', 'Automated Insights', 'Real-time Processing'],
      gradient: 'from-blue-500 to-purple-600'
    },
    {
      id: 2,
      title: 'Secure Infrastructure',
      subtitle: 'Enterprise-Grade Security',
      description: 'Every enterprise deserves complete control over its data and systems.',
      details: 'Private, extensible, cloud-agnostic architectures that ensure security without sacrificing innovation.',
      features: ['Zero-Trust Architecture', 'End-to-End Encryption', 'Multi-Cloud Support', 'Compliance Ready'],
      gradient: 'from-green-500 to-teal-600'
    },
    {
      id: 3,
      title: 'Applied Research',
      subtitle: 'Cutting-Edge Innovation',
      description: 'We push boundaries in data systems and natural language understanding.',
      details: 'Experimental research transformed into practical, production-ready solutions.',
      features: ['Advanced NLP Models', 'Knowledge Graphs', 'Semantic Search', 'Contextual AI'],
      gradient: 'from-purple-500 to-pink-600'
    }
  ];

  return (
    <section id="focus" className="our-focus" ref={sectionRef}>
      <div className="container">
        <div className={`section-content ${isVisible ? 'section-content--visible' : ''}`}>
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-text">Our Focus</span>
            </div>
            <h2 className="section-title">
              Three Pillars of <span className="title-accent">Innovation</span>
            </h2>
            <p className="section-description">
              Our expertise spans across intelligent product development, secure infrastructure, 
              and cutting-edge research — delivering comprehensive AI solutions for modern enterprises.
            </p>
          </div>
          
          <div className="focus-container">
            <div className="focus-navigation">
              {focusAreas.map((area, index) => (
                <button
                  key={area.id}
                  className={`focus-nav-item ${activeCard === index ? 'focus-nav-item--active' : ''}`}
                  onClick={() => setActiveCard(index)}
                >
                  <div className="nav-item-content">
                    <div className="nav-item-title">{area.title}</div>
                    <div className="nav-item-subtitle">{area.subtitle}</div>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="focus-content">
              {focusAreas.map((area, index) => (
                <div
                  key={area.id}
                  className={`focus-card ${activeCard === index ? 'focus-card--active' : ''}`}
                >
                  <div className="card-background">
                    <div className={`card-gradient ${area.gradient}`}></div>
                  </div>
                  
                  <div className="card-content">
                    <div className="card-header">
                      <div className="card-number">0{area.id}</div>
                    </div>
                    
                    <div className="card-main">
                      <h3 className="card-title">{area.title}</h3>
                      <p className="card-description">{area.description}</p>
                      <p className="card-details">{area.details}</p>
                    </div>
                    
                    <div className="card-features">
                      <h4 className="features-title">Key Capabilities</h4>
                      <ul className="features-list">
                        {area.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="feature-item">
                            <svg className="feature-check" viewBox="0 0 20 20">
                              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                            </svg>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="focus-summary">
            <div className="summary-card">
              <h3 className="summary-title">Integrated Approach</h3>
              <p className="summary-text">
                Our three focus areas work in harmony to deliver comprehensive AI solutions 
                that are intelligent, secure, and future-ready.
              </p>
              <div className="summary-stats">
                <div className="stat">
                  <div className="stat-value">100%</div>
                  <div className="stat-label">Secure by Design</div>
                </div>
                <div className="stat">
                  <div className="stat-value">24/7</div>
                  <div className="stat-label">Support & Monitoring</div>
                </div>
                <div className="stat">
                  <div className="stat-value">&lt; 1s</div>
                  <div className="stat-label">Response Time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurFocus;
